import { useId, type SelectHTMLAttributes } from 'react';

import { cn } from '@/shared/lib/cn';

import { FieldLabel } from './FieldLabel';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'id' | 'children'
> {
  label: string;
  options: SelectOption[];
  placeholder?: string | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  /**
   * Texto do icone de ajuda (i). Sem ele, o campo usa o dicionario de ajuda pela
   * tela e pelo rotulo; false desliga a ajuda.
   */
  help?: string | false | undefined;
  /**
   * Rotulo so para o leitor de tela, como no campo de texto: a lista em linha de
   * tabela (o prazo de cada coluna da esteira, P-EST-1) nao repete o rotulo visivel.
   */
  labelHidden?: boolean | undefined;
}

/** Selecao com rotulo e erro associados ao controle, como o campo de texto. */
export function Select({
  label,
  options,
  placeholder,
  error,
  hint,
  help,
  labelHidden,
  className,
  ...rest
}: SelectProps) {
  const selectId = useId();
  const hintId = `${selectId}-hint`;
  const errorId = `${selectId}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel
        htmlFor={selectId}
        label={label}
        className={labelHidden ? 'sr-only' : 'text-xs font-semibold text-ink-700'}
        help={labelHidden ? false : help}
      />
      <select
        id={selectId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={cn(
          'h-10 w-full rounded-control border bg-surface px-3 text-sm text-ink-900 shadow-(--shadow-xs)',
          'transition-[border-color,box-shadow] duration-(--duration-fast)',
          'focus:outline-none focus:ring-4',
          error
            ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/15'
            : 'border-line-strong hover:border-ink-400 focus:border-brand-500 focus:ring-brand-500/15',
          className,
        )}
        {...rest}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hint && !error ? (
        <p id={hintId} className="text-2xs text-ink-500">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-2xs font-medium text-danger-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
